import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import TruckLogo from './TruckLogo';
import BusinessFooter from './BusinessFooter';

interface TermsAndConditionsProps {
  onClose: () => void;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ onClose }) => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-primary text-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <button 
              onClick={onClose}
              className="mr-4 p-2 rounded-full hover:bg-primary-dark transition-colors"
              aria-label={t('common.back')}
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center">
              <TruckLogo size={28} className="text-white mr-2" />
              <h1 className="text-xl font-bold uppercase">COTIZATRIN</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">{t('terms.title')}</h1>
          
          <div className="prose max-w-none text-sm sm:text-base">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.introduction.title')}</h2>
            <p className="mb-3">
              {t('terms.introduction.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.definitions.title')}</h2>
            <p className="mb-2">{t('terms.definitions.intro')}:</p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('terms.definitions.cotizatrin')}</li>
              <li className="mb-1">{t('terms.definitions.user')}</li>
              <li className="mb-1">{t('terms.definitions.service')}</li>
              <li className="mb-1">{t('terms.definitions.quotation')}</li>
            </ul>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.serviceUse.title')}</h2>
            <p className="mb-2">{t('terms.serviceUse.intro')}:</p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('terms.serviceUse.item1')}</li>
              <li className="mb-1">{t('terms.serviceUse.item2')}</li>
              <li className="mb-1">{t('terms.serviceUse.item3')}</li>
              <li className="mb-1">{t('terms.serviceUse.item4')}</li>
              <li className="mb-1">{t('terms.serviceUse.item5')}</li>
            </ul>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.quotations.title')}</h2>
            <p className="mb-3">
              {t('terms.quotations.content')}
            </p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('terms.quotations.item1')}</li>
              <li className="mb-1">{t('terms.quotations.item2')}</li>
              <li className="mb-1">{t('terms.quotations.item3')}</li>
              <li className="mb-1">{t('terms.quotations.item4')}</li>
            </ul>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.userAccounts.title')}</h2>
            <p className="mb-3">
              {t('terms.userAccounts.content')}
            </p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('terms.userAccounts.item1')}</li>
              <li className="mb-1">{t('terms.userAccounts.item2')}</li>
              <li className="mb-1">{t('terms.userAccounts.item3')}</li>
              <li className="mb-1">{t('terms.userAccounts.item4')}</li>
            </ul>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.privacy.title')}</h2>
            <p className="mb-3">
              {t('terms.privacy.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.intellectualProperty.title')}</h2>
            <p className="mb-3">
              {t('terms.intellectualProperty.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.liability.title')}</h2>
            <p className="mb-3">
              {t('terms.liability.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.modifications.title')}</h2>
            <p className="mb-3">
              {t('terms.modifications.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.applicableLaw.title')}</h2>
            <p className="mb-3">
              {t('terms.applicableLaw.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('terms.contact.title')}</h2>
            <p className="mb-3">
              {t('terms.contact.content')} 
              <a href="mailto:info@cotizatrin.com" className="text-primary hover:underline"> info@cotizatrin.com</a>.
            </p>

            <div className="mt-6 pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                {t('terms.lastUpdate')}
              </p>
            </div>
          </div>
        </div>
      </main>

      <BusinessFooter variant="minimal" />
    </div>
  );
};

export default TermsAndConditions;